var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import EditableToggle from './EditableToggle';
var messages = defineMessages({
    InvalidPhoneNumber: {
        id: 'msgInvalidPhoneNumber',
        defaultMessage: 'Please enter a valid phone number',
    },
});
var UTILS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.0/js/utils.js';
var EditablePhoneInput = function (_a) {
    var value = _a.value, onChange = _a.onChange, country = _a.country, _b = _a.readOnly, readOnly = _b === void 0 ? false : _b, _c = _a.placeholder, placeholder = _c === void 0 ? '' : _c, onError = _a.onError;
    var intl = useIntl();
    var itiRef = useRef(null);
    var inputElRef = useRef(null);
    var snapshotRef = useRef({ number: '', valid: true });
    var intendedCountryRef = useRef((country === null || country === void 0 ? void 0 : country.toLowerCase()) || '');
    var fromDropdownRef = useRef(false);
    var submitRef = useRef(function () { });
    var _d = useState(false), hasError = _d[0], setHasError = _d[1];
    var onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    var intlRef = useRef(intl);
    intlRef.current = intl;
    var setHasErrorRef = useRef(setHasError);
    setHasErrorRef.current = setHasError;
    useEffect(function () {
        return function () {
            if (itiRef.current) {
                itiRef.current.destroy();
                itiRef.current = null;
            }
        };
    }, []);
    useEffect(function () {
        if (itiRef.current && country) {
            var code = country.toLowerCase();
            intendedCountryRef.current = code;
            itiRef.current.setCountry(code);
        }
    }, [country]);
    var captureSnapshot = useCallback(function () {
        if (itiRef.current) {
            snapshotRef.current = {
                number: itiRef.current.getNumber(1) || '',
                valid: itiRef.current.isValidNumber(),
            };
        }
    }, []);
    var showValidationError = useCallback(function () {
        onError === null || onError === void 0 ? void 0 : onError(intl.formatMessage(messages.InvalidPhoneNumber));
        setHasError(true);
    }, [onError, intl]);
    var validateAndSubmit = useCallback(function (submit) {
        captureSnapshot();
        var _a = snapshotRef.current, number = _a.number, valid = _a.valid;
        if (!number) {
            setHasError(false);
            submit();
            return;
        }
        if (!valid) {
            showValidationError();
            return;
        }
        setHasError(false);
        submit();
    }, [captureSnapshot, showValidationError]);
    var handleSubmit = useCallback(function (_editValue) {
        var number = snapshotRef.current.number;
        var currentNumber = number || _editValue;
        if (!currentNumber) {
            return onChange('');
        }
        return onChange(currentNumber);
    }, [onChange]);
    var initIti = useCallback(function (el, currentValue) {
        var _a;
        var intlTelInput = window.intlTelInput;
        if (!intlTelInput)
            return;
        itiRef.current = intlTelInput(el, {
            nationalMode: true,
            autoPlaceholder: 'aggressive',
            initialCountry: intendedCountryRef.current || (country === null || country === void 0 ? void 0 : country.toLowerCase()) || '',
            preferredCountries: ['be', 'nl', 'fr', 'lu', 'es'],
            utilsScript: UTILS_URL,
        });
        var numberToRestore = snapshotRef.current.number || currentValue;
        if (numberToRestore) {
            itiRef.current.setNumber(numberToRestore);
            intendedCountryRef.current =
                ((_a = itiRef.current.getSelectedCountryData()) === null || _a === void 0 ? void 0 : _a.iso2) || intendedCountryRef.current;
            snapshotRef.current = {
                number: snapshotRef.current.number || numberToRestore,
                valid: snapshotRef.current.valid,
            };
        }
        else if (country) {
            itiRef.current.setCountry(country.toLowerCase());
        }
        el.addEventListener('open:countrydropdown', function () {
            fromDropdownRef.current = true;
        });
        el.addEventListener('close:countrydropdown', function () {
            setTimeout(function () {
                fromDropdownRef.current = false;
            }, 200);
        });
        el.addEventListener('countrychange', function () {
            var _a, _b;
            if (!itiRef.current)
                return;
            if (fromDropdownRef.current) {
                intendedCountryRef.current =
                    ((_a = itiRef.current.getSelectedCountryData()) === null || _a === void 0 ? void 0 : _a.iso2) || intendedCountryRef.current;
                setTimeout(function () {
                    var _a;
                    captureSnapshot();
                    if (snapshotRef.current.number && !snapshotRef.current.valid) {
                        (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, intlRef.current.formatMessage(messages.InvalidPhoneNumber));
                        setHasErrorRef.current(true);
                    }
                    else {
                        setHasErrorRef.current(false);
                        submitRef.current();
                    }
                }, 100);
            }
            else if (intendedCountryRef.current) {
                var current = (_b = itiRef.current.getSelectedCountryData()) === null || _b === void 0 ? void 0 : _b.iso2;
                if (current !== intendedCountryRef.current) {
                    itiRef.current.setCountry(intendedCountryRef.current);
                }
            }
        });
    }, [country]);
    return (_jsx(EditableToggle, __assign({ onSubmit: handleSubmit, value: value, readOnly: readOnly, onModeChange: function (mode) {
            if (mode === 'display') {
                if (itiRef.current) {
                    itiRef.current.destroy();
                    itiRef.current = null;
                }
            }
        } }, { children: function (_a) {
            var inputValue = _a.inputValue, handleChange = _a.handleChange, toggleInputRef = _a.inputRef, submit = _a.submit, cancel = _a.cancel, handleKeyDown = _a.handleKeyDown;
            submitRef.current = submit;
            return (_jsx("div", __assign({ className: hasError ? 'has-error' : undefined }, { children: _jsx("input", { onChange: function (e) {
                        handleChange(e);
                        captureSnapshot();
                        setHasError(false);
                    }, ref: function (el) {
                        toggleInputRef(el);
                        if (!el)
                            return;
                        if (el === inputElRef.current)
                            return;
                        if (itiRef.current) {
                            itiRef.current.destroy();
                            itiRef.current = null;
                        }
                        inputElRef.current = el;
                        initIti(el, inputValue);
                    }, onBlur: function () {
                        captureSnapshot();
                        setTimeout(function () {
                            var _a;
                            if (fromDropdownRef.current)
                                return;
                            var itiContainer = (_a = inputElRef.current) === null || _a === void 0 ? void 0 : _a.closest('.iti');
                            var stillInIti = itiContainer && itiContainer.contains(document.activeElement);
                            if (!stillInIti && document.activeElement !== inputElRef.current) {
                                validateAndSubmit(submit);
                            }
                        }, 100);
                    }, onKeyDown: function (e) {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            validateAndSubmit(submit);
                            return;
                        }
                        if (e.key === 'Escape') {
                            setHasError(false);
                            handleKeyDown(e);
                            return;
                        }
                        handleKeyDown(e);
                    }, className: "form-control", placeholder: placeholder, autoComplete: "off", type: "tel" }) })));
        } })));
};
export default EditablePhoneInput;
